import React, { useMemo } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { isPatronScreeningPrototypeEnabled } from '../../../config/featureFlags'
import PatronScreeningPrototype from './PatronScreeningPrototype'

/** Split a display name into first + remainder for the screening intake form. */
export function splitDisplayNameForScreening(displayName) {
  const t = (displayName || '').trim()
  if (!t) return { firstName: '', lastName: '' }
  const parts = t.split(/\s+/)
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

/**
 * ECDD tool: opens the patron screening prototype (docs/reference) in a modal.
 * Demo / reference UI — not connected to live screening APIs.
 */
export default function PatronScreeningToolModal({ open, onClose, memberName, memberId }) {
  const { firstName, lastName } = useMemo(() => splitDisplayNameForScreening(memberName), [memberName])

  if (!open) return null
  if (!isPatronScreeningPrototypeEnabled()) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col border border-gray-100">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-psg-teal m-0">Patron screening</h3>
            <p className="text-xs text-gray-500 mt-1 m-0 leading-snug max-w-xl">
              Unified background check and due diligence screening for AML/KYC compliance.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 shrink-0"
            aria-label="Close patron screening"
          >
            <XMarkIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 bg-slate-50/90">
          <PatronScreeningPrototype
            initialFirstName={firstName}
            initialLastName={lastName}
            patronId={memberId}
          />
        </div>
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex justify-end shrink-0">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
